import React, { useState, useEffect } from 'react';
import { X, User, Building2, Check, CreditCard, Info, AlertCircle, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../utils/currency';
import { sendFormNotification } from './EmailService';

interface TerangaPlusReservationFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedColor: string;
}

type ProfileType = 'particulier' | 'entreprise';
type PackageType = 'essentiel' | 'complet';

const TerangaPlusReservationForm: React.FC<TerangaPlusReservationFormProps> = ({
  isOpen,
  onClose,
  selectedColor
}) => {
  const [profileType, setProfileType] = useState<ProfileType>('particulier');
  const [packageType, setPackageType] = useState<PackageType>('complet');
  const [quantite, setQuantite] = useState<string>('1');
  const [acompte, setAcompte] = useState<string>('50000');
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    entreprise: '',
    adresse: '',
    ville: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const PRICES = {
    essentiel: 850000,
    complet: 950000
  };

  const quantiteNumber = parseInt(quantite) || 1;
  const totalPrice = PRICES[packageType] * quantiteNumber;
  const MIN_ACOMPTE = 50000;
  const MAX_ACOMPTE = totalPrice;

  useEffect(() => {
    const currentAcompte = parseInt(acompte) || MIN_ACOMPTE;
    if (currentAcompte > totalPrice) {
      setAcompte(totalPrice.toString());
    }
  }, [quantite, packageType]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuantiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    const num = parseInt(value) || 1;
    if (num >= 1 && num <= 100) {
      setQuantite(num.toString());
    }
  };

  const handleAcompteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setAcompte(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const acompteNumber = parseInt(acompte);
    const qte = parseInt(quantite) || 1;

    if (qte < 1) {
      toast.error('La quantité minimum est de 1');
      return;
    }

    if (!acompteNumber || acompteNumber < MIN_ACOMPTE) {
      toast.error(`L'acompte minimum est de ${formatCurrency(MIN_ACOMPTE)}`);
      return;
    }

    if (acompteNumber > totalPrice) {
      toast.error(`L'acompte ne peut pas dépasser ${formatCurrency(totalPrice)}`);
      return;
    }

    if (profileType === 'entreprise' && !formData.entreprise) {
      toast.error('Veuillez renseigner le nom de votre entreprise');
      return;
    }

    setIsSubmitting(true);

    try {
      const reservationData = {
        ...formData,
        profileType: profileType === 'particulier' ? 'Particulier' : 'Entreprise',
        packageType: packageType === 'essentiel' ? 'Pack Essentiel (850 000 FCFA)' : 'Pack Complet (950 000 FCFA)',
        quantite: qte,
        packagePrice: PRICES[packageType],
        totalPrice,
        acompte: acompteNumber,
        solde: totalPrice - acompteNumber,
        couleur: selectedColor,
        produit: 'Teranga+',
        paymentMethod: 'Wave',
        dateReservation: new Date().toLocaleString('fr-FR')
      };

      console.log('Réservation Teranga+:', reservationData);

      // Envoyer notification email
      const emailSent = await sendFormNotification('Réservation Teranga+', reservationData);

      if (!emailSent) {
        console.warn('Email notification may not have been sent');
      }

      toast.success('Réservation reçue ! Nous vous contacterons sous 24h.');

      setTimeout(() => {
        onClose();
        setFormData({
          nom: '',
          prenom: '',
          email: '',
          telephone: '',
          entreprise: '',
          adresse: '',
          ville: ''
        });
        setQuantite('1');
        setAcompte('50000');
      }, 1500);

    } catch (error) {
      console.error('Erreur réservation:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const resteToPay = totalPrice - parseInt(acompte || '0');

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col my-2 sm:my-4">
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-[#00A859] to-[#0E9F6E] p-4 sm:p-6 rounded-t-2xl relative flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full text-white transition-colors"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>

          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2 pr-10">
            🏍️ Réserver votre Teranga+
          </h2>
          <p className="text-green-50 text-sm">
            Couleur sélectionnée : <strong>{selectedColor}</strong>
          </p>
        </div>

        {/* Form Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Type de profil */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Type de profil *
              </label>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => setProfileType('particulier')}
                  className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    profileType === 'particulier'
                      ? 'border-[#00A859] bg-green-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-[#00A859]/50'
                  }`}
                >
                  <User size={20} className={profileType === 'particulier' ? 'text-[#00A859]' : 'text-gray-400'} />
                  <div className="text-left flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm sm:text-base truncate">Particulier</div>
                    <div className="text-xs text-gray-500 hidden sm:block">Usage personnel</div>
                  </div>
                  {profileType === 'particulier' && (
                    <Check size={18} className="text-[#00A859] flex-shrink-0" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setProfileType('entreprise')}
                  className={`flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border-2 transition-all ${
                    profileType === 'entreprise'
                      ? 'border-[#00A859] bg-green-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-[#00A859]/50'
                  }`}
                >
                  <Building2 size={20} className={profileType === 'entreprise' ? 'text-[#00A859]' : 'text-gray-400'} />
                  <div className="text-left flex-1 min-w-0">
                    <div className="font-medium text-gray-900 text-sm sm:text-base truncate">Entreprise</div>
                    <div className="text-xs text-gray-500 hidden sm:block">Usage professionnel</div>
                  </div>
                  {profileType === 'entreprise' && (
                    <Check size={18} className="text-[#00A859] flex-shrink-0" />
                  )}
                </button>
              </div>
            </div>

            {/* Choix du pack */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Choisissez votre pack *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Pack Essentiel */}
                <div
                  onClick={() => setPackageType('essentiel')}
                  className={`cursor-pointer p-4 sm:p-5 rounded-xl border-2 transition-all ${
                    packageType === 'essentiel'
                      ? 'border-[#00A859] bg-green-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-[#00A859]/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-gray-900 text-base sm:text-lg">Essentiel</h3>
                      <div className="text-xl sm:text-2xl font-bold text-[#00A859] mt-1">
                        {formatCurrency(PRICES.essentiel)}
                      </div>
                    </div>
                    {packageType === 'essentiel' && (
                      <Check size={24} className="text-[#00A859]" />
                    )}
                  </div>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center text-sm text-gray-700">
                      <X size={14} className="text-red-500 mr-2 flex-shrink-0" />
                      <span>Sans carte grise</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <X size={14} className="text-red-500 mr-2 flex-shrink-0" />
                      <span>Sans plaque d'immatriculation</span>
                    </div>
                  </div>

                  <div className="bg-[#00A859]/10 border border-[#00A859]/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Info size={16} className="text-[#00A859] mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-900 font-medium">
                        🎁 Cadeau spécial : Assurance maladies – 12 mois
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pack Complet */}
                <div
                  onClick={() => setPackageType('complet')}
                  className={`cursor-pointer p-4 sm:p-5 rounded-xl border-2 transition-all relative ${
                    packageType === 'complet'
                      ? 'border-[#00A859] bg-green-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-[#00A859]/50'
                  }`}
                >
                  <div className="absolute top-2 right-2 bg-[#00A859] text-white text-xs font-bold px-2 py-1 rounded-full">
                    RECOMMANDÉ
                  </div>

                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-gray-900 text-base sm:text-lg">Complet</h3>
                      <div className="text-xl sm:text-2xl font-bold text-[#00A859] mt-1">
                        {formatCurrency(PRICES.complet)}
                      </div>
                    </div>
                    {packageType === 'complet' && (
                      <Check size={24} className="text-[#00A859]" />
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-700">
                      <Check size={14} className="text-[#00A859] mr-2 flex-shrink-0" />
                      <span>Carte grise</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Check size={14} className="text-[#00A859] mr-2 flex-shrink-0" />
                      <span>Plaque d'immatriculation</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Check size={14} className="text-[#00A859] mr-2 flex-shrink-0" />
                      <span>Assurance RC</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Check size={14} className="text-[#00A859] mr-2 flex-shrink-0" />
                      <span>Assurance Accident – 12 mois</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700">
                      <Check size={14} className="text-[#00A859] mr-2 flex-shrink-0" />
                      <span>Assurance Maladie – 12 mois</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quantité désirée */}
            <div className="bg-gradient-to-r from-green-50 to-white border border-[#00A859]/30 rounded-xl p-4 sm:p-5">
              <label className="block text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package size={18} className="text-[#00A859]" />
                Quantité désirée *
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <input
                    type="number"
                    name="quantite"
                    value={quantite}
                    onChange={handleQuantiteChange}
                    min="1"
                    max="100"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none text-lg font-semibold"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Min: 1 • Max: 100</p>
                </div>

                <div className="bg-white rounded-lg p-3 border-2 border-[#00A859]/50">
                  <div className="text-xs text-gray-600 mb-1">Montant total</div>
                  <div className="text-2xl font-bold text-[#00A859]">
                    {formatCurrency(totalPrice)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {quantiteNumber} × {formatCurrency(PRICES[packageType])}
                  </div>
                </div>
              </div>
            </div>

            {/* Montant de l'acompte */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 sm:p-5">
              <label className="block text-sm font-semibold text-gray-900 mb-3">
                Montant de l'acompte (via Wave) *
              </label>

              <div className="relative mb-3">
                <input
                  type="text"
                  name="acompte"
                  value={acompte}
                  onChange={handleAcompteChange}
                  placeholder="50000"
                  className="w-full px-4 py-3 pr-20 border-2 border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none text-lg font-semibold"
                  required
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                  FCFA
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-white rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600">Minimum</div>
                  <div className="font-medium text-gray-900 text-sm">{formatCurrency(MIN_ACOMPTE)}</div>
                </div>
                <div className="bg-white rounded-lg p-2 border border-gray-200">
                  <div className="text-xs text-gray-600">Maximum</div>
                  <div className="font-medium text-gray-900 text-sm">{formatCurrency(MAX_ACOMPTE)}</div>
                </div>
              </div>

              {resteToPay > 0 && (
                <div className="bg-white rounded-lg p-3 sm:p-4 border-2 border-blue-300 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-base text-gray-700 font-medium">Reste à payer :</span>
                    <span className="text-xl sm:text-2xl font-bold text-[#00A859]">
                      {formatCurrency(resteToPay)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Informations personnelles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom *
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none"
                  required
                />
              </div>

              {profileType === 'entreprise' && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'entreprise *
                  </label>
                  <input
                    type="text"
                    name="entreprise"
                    value={formData.entreprise}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none"
                    required={profileType === 'entreprise'}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleInputChange}
                  placeholder="+221 XX XXX XX XX"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ville *
                </label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse
                </label>
                <input
                  type="text"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-[#00A859] focus:ring-2 focus:ring-green-200 outline-none"
                />
              </div>
            </div>

            {/* Note importante */}
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-900 flex-1 min-w-0">
                <p className="font-medium mb-1">Paiement via Wave uniquement</p>
                <p className="text-xs">
                  Après validation de votre réservation, vous recevrez les instructions de paiement Wave par SMS et email.
                </p>
              </div>
            </div>
          </form>
        </div>

        {/* Footer with buttons - Fixed */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4 sm:p-6 bg-gray-50 rounded-b-2xl">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-[#00A859] to-[#0E9F6E] text-white rounded-lg hover:from-[#00964F] hover:to-[#0D8F63] transition-all font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  Réserver maintenant
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerangaPlusReservationForm;
